import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Button,
  HStack,
  Text,
  Container,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useToast,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FiTruck, FiAnchor } from 'react-icons/fi';
import { logout } from '../../store/slices/authSlice';

/**
 * Головний Layout компонент з навігацією
 */
const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef();

  const { user } = useSelector((state) => state.auth);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    fetch('/api/calculator/logistics-review-count', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setReviewCount(d.count || 0))
      .catch(() => {});
  }, [user]);

  // Close drawer whenever the route changes — called unconditionally
  // so that onClose() always fires, even if a race left isOpen stale.
  useEffect(() => {
    onClose();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety-net: after close, clear any stale body styles / aria-hidden
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        const root = document.getElementById('root');
        if (root && root.getAttribute('aria-hidden') === 'true') {
          root.removeAttribute('aria-hidden');
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  /**
   * Обробка виходу з системи
   */
  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();

      toast({
        title: 'Вихід виконано',
        description: 'Ви успішно вийшли з системи',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/login');
    } catch (error) {
      toast({
        title: 'Помилка',
        description: 'Не вдалося вийти з системи',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * Навігаційне меню
   */
  const NavLinks = ({ isMobile = false, onClose = () => { }, reviewCount: rc = 0 }) => {
    const linkProps = isMobile
      ? { width: 'full', justifyContent: 'flex-start', minH: '44px' }
      : {};

    const getNavProps = (path) => {
      if (isMobile) {
        return { as: RouterLink, to: path, onClick: onClose };
      }
      return { as: RouterLink, to: path };
    };

    const isActive = (path) => location.pathname.startsWith(path);

    return (
      <>
        {user?.role === 'admin' && (
          <Button
            variant={isActive('/users') ? 'solid' : 'ghost'}
            colorScheme="blue"
            {...linkProps}
            {...getNavProps('/users')}
          >
            Користувачі
          </Button>
        )}

        <Button
          variant={isActive('/clients') ? 'solid' : 'ghost'}
          colorScheme="blue"
          {...linkProps}
          {...getNavProps('/clients')}
        >
          Клієнти
        </Button>

        <Menu>
          <MenuButton
            as={Button}
            variant={isActive('/cars') ? 'solid' : 'ghost'}
            colorScheme="blue"
            rightIcon={<ChevronDownIcon />}
            {...linkProps}
          >
            Каталог авто
          </MenuButton>
          <MenuList>
            <MenuItem
              as={RouterLink}
              to="/cars"
              onClick={isMobile ? onClose : undefined}
              fontWeight={location.pathname === '/cars' ? 'bold' : 'normal'}
              bg={location.pathname === '/cars' ? 'blue.50' : undefined}
              color={location.pathname === '/cars' ? 'blue.600' : undefined}
            >
              <><FiTruck style={{verticalAlign:'middle',marginRight:'4px'}}/> Каталог авто</>
            </MenuItem>
            <MenuItem
              as={RouterLink}
              to="/cars/in-transit"
              onClick={isMobile ? onClose : undefined}
              fontWeight={location.pathname.startsWith('/cars/in-transit') ? 'bold' : 'normal'}
              bg={location.pathname.startsWith('/cars/in-transit') ? 'blue.50' : undefined}
              color={location.pathname.startsWith('/cars/in-transit') ? 'blue.600' : undefined}
            >
              <><FiAnchor style={{verticalAlign:'middle',marginRight:'4px'}}/> Авто в дорозі</>
            </MenuItem>
          </MenuList>
        </Menu>

        <Button
          variant={isActive('/leads') ? 'solid' : 'ghost'}
          colorScheme="blue"
          {...linkProps}
          {...getNavProps('/leads')}
        >
          Ліди TMA
        </Button>

        {user?.role === 'admin' && (
          <Button
            variant={isActive('/calculator-settings') ? 'solid' : 'ghost'}
            colorScheme="blue"
            {...linkProps}
            {...getNavProps('/calculator-settings')}
          >
            Калькулятор
            {rc > 0 && (
              <Badge colorScheme="orange" ml={1} borderRadius="full" fontSize="0.7em">
                {rc}
              </Badge>
            )}
          </Button>
        )}

        <Button
          variant={isActive('/archive') ? 'solid' : 'ghost'}
          colorScheme="blue"
          {...linkProps}
          {...getNavProps('/archive')}
        >
          Архів
        </Button>
      </>
    );
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" boxShadow="sm" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl">
          <Flex h={16} alignItems="center" justifyContent="space-between">
            {/* Logo */}
            <Text fontSize="xl" fontWeight="bold" color="teal.700">
              CRM AVTOSVIT
            </Text>

            {/* Desktop Navigation */}
            <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
              <NavLinks reviewCount={reviewCount} />
            </HStack>

            {/* User Menu */}
            <HStack spacing={4}>
              {/* ✅ НОВA КНОПКА - Завантажити Акт */}
              <Button
                as="a"
                href="/Акт_прийому-передачі.docx"
                download="Акт_прийому-передачі.docx"
                colorScheme="teal"
                variant="outline"
                size="sm"
                display={{ base: 'none', md: 'flex' }}
              >
                Завантажити Акт
              </Button>

              {/* Mobile Menu Button */}
              <IconButton
                ref={btnRef}
                display={{ base: 'flex', md: 'none' }}
                onClick={onOpen}
                variant="ghost"
                aria-label="Open menu"
                icon={<HamburgerIcon />}
                minW="44px"
                minH="44px"
              />

              {/* User Avatar Menu */}
              <Menu>
                <MenuButton>
                  <HStack spacing={2}>
                    <Avatar size="sm" name={user?.full_name} />
                    <Text display={{ base: 'none', md: 'block' }}>
                      {user?.full_name}
                    </Text>
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem isDisabled>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold">{user?.full_name}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {user?.role === 'admin' ? 'Адміністратор' : 'Менеджер'}
                      </Text>
                    </VStack>
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem onClick={handleLogout} color="red.500">
                    Вийти
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Navigation Drawer — key forces full unmount on route
          change so Chakra's internal modal-manager count resets to 0 */}
      <Drawer
        key={location.pathname}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
        returnFocusOnClose={false}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Меню</DrawerHeader>
          <DrawerBody>
            <VStack spacing={2} align="stretch">
              <NavLinks isMobile onClose={onClose} reviewCount={reviewCount} />

              {/* ✅ НОВA КНОПКА - Завантажити Акт (мобільна версія) */}
              <Button
                as="a"
                href="/Акт_прийому-передачі.docx"
                download="Акт_прийому-передачі.docx"
                colorScheme="teal"
                variant="outline"
                width="full"
                onClick={onClose}
              >
                Завантажити Акт
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Box w="100%" px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;